/**
 * Store: Product Card.
 * -----------------------------------------------------------------------------
 * Root store for any product card data.
 *
 */

import productsQuery from '~/graphql/queries/products.gql'
import { formatProduct } from '~/helpers/format'

export default {
  namespaced: true,
  state: {
    relatedProducts: {}, // Keyed by tag
    relatedShopifyProducts: {}, // Keyed by product id
    loadingTags: {}, // Tracks loading state for specific tags
    quickAddProduct: null,
    size: null,
    colourFamily: null,
  },
  mutations: {
    SET_RELATED_PRODUCTS(state, { tag, products }) {
      state.relatedProducts[tag] = products
    },
    SET_SHOPIFY_PRODUCTS(state, { id, product }) {
      state.relatedShopifyProducts[id] = product
    },
    SET_LOADING(state, { tag, isLoading }) {
      state.loadingTags[tag] = isLoading
    },
    SET_QUICK_ADD_PRODUCT(state, product) {
      state.quickAddProduct = product
    },
    SET_SIZE(state, size) {
      state.size = size
    },
    SET_COLOUR_FAMILY(state, family) {
      state.colourFamily = family
    },
  },
  actions: {
    async fetchProductsByTag({ commit, state }, tag) {
      // If already fetched, skip fetching
      if (!tag || state.relatedProducts[tag]) return

      // Set loading state
      commit('SET_LOADING', { tag, isLoading: true })

      try {
        // Fetch products from the Storefront API
        const response = await cnvs.Query({
          query: productsQuery,
          variables: {
            country: cnvs.store.country,
            query: `tag:${tag}`,
            language: cnvs.store.language,
          },
        })

        if (!response?.products?.edges?.length) {
          return
        }

        const products =
          response.products.edges.map(edge => formatProduct({ product: edge.node }))

        // Save fetched products
        commit('SET_RELATED_PRODUCTS', { tag, products })

        products.forEach((product) => {
          commit('SET_SHOPIFY_PRODUCTS', { id: product.id, product })
        })
      } catch (error) {
        console.error(`Failed to fetch products for tag "${tag}":`, error)
      } finally {
        // Clear loading state
        commit('SET_LOADING', { tag, isLoading: false })
      }
    },

    /**
     * Set product.
     * @param {Object} product - Current PDP product.
     */
    setQuickAddProduct({ commit, dispatch }, product) {
      commit('SET_QUICK_ADD_PRODUCT', product)

      /**
       * Open quick view overlay if setting a product.
       */
      if (!product) {
        return
      }

      const data = {
        component: 'quick-view',
        ignoreDismissed: true,
        namespace: 'quickView',
      }

      dispatch('overlays/open', data, { root: true })
    },

    /**
     * Sets the current product size category to state.
     * @param {String} size - The size category of the current product.
     */
    setCurrentSize({ commit }, size) {
      commit('SET_SIZE', size)
    },

    /**
     * Set colour family.
     * @param {String} family - String value of the colour family for current product.
     */
     setColourFamily({ commit }, family) {
      commit('SET_COLOUR_FAMILY', family)
    },
  },
  getters: {
    getRelatedProductsByTag: state => tag => state.relatedProducts[tag] || [],
    getShopifyProductsById: state => id => state.relatedShopifyProducts[id] || null,
    isTagLoading: state => tag => state.loadingTags[tag] || false,

    /**
     * Get store quick add product.
     * @returns {object | null}
     */
    getQuickAddProduct: (state) => () => {
      return state.quickAddProduct
    },
    getCurrentSize: (state) => () => {
      return state.size
    },
    getColourFamily: (state) => () => {
      return state.colourFamily
    },
  },
}
